/**
 * Hook System — mecânica central. Botão direito lança uma corrente que VIAJA (não
 * teleporta), gruda num inimigo e o PUXA até perto do player. Estados claros;
 * erra → cooldown curto, acerta → cooldown normal (sem spam). O inimigo puxado
 * continua sendo um Enemy normal (mantém HP, pode ser atacado/morrer).
 *
 * Arquitetura pronta (não implementado): hookDamage, doubleHook, chainHook,
 * hookStun, hookExplosion, elitePull, bossInteraction.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import type { GameInput } from "../core/GameInput";
import type { Sanctuary } from "../world/Sanctuary";
import { clamp } from "../utils/math";

export type HookState = "ready" | "firing" | "pulling" | "returning" | "cooldown";

/**
 * Como um alvo reage à fisgada:
 *  - "pull": puxável normal (Voidlings/Runners/Brutes)
 *  - "grab": a corrente prende mas NÃO puxa — o alvo já aplicou seu efeito
 *    (Elite = stagger; Boss = interromper Howl). Cooldown normal.
 *  - "none": a corrente ricocheteia (ex.: Boss fora da janela de interrupção). Cooldown curto.
 */
export type HookReaction = "pull" | "grab" | "none";

/** O que o Hook consegue agarrar (Enemy/Boss satisfazem estruturalmente). */
export interface HookTarget {
  readonly position: THREE.Vector3;
  readonly radius: number;
  readonly isDead: boolean;
  pulled: boolean;
  /** Opcional: o alvo decide como reage à fisgada. Ausente => "pull". */
  onHooked?(): HookReaction;
}

const _aim = new THREE.Vector3();
const _hit = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _LINK_SCALE = new THREE.Vector3(1, 1, 1);

export class HookSystem {
  state: HookState = "ready";
  onThrow?: () => void;

  private readonly cfg = GameConfig.hook;
  private rangeMult = 1; // Void Tree: hook range
  private cdMult = 1; // Void Tree: hook cooldown (1 - reduc)

  /** Aplica mods da Void Tree ao hook (alcance/recarga). */
  setModifiers(rangeMult: number, cdReduc: number): void {
    this.rangeMult = 1 + rangeMult;
    this.cdMult = 1 - cdReduc;
  }
  /** Injeta a Sanctuary: o pull nunca deposita um inimigo dentro da safe zone. */
  setSanctuary(sanctuary: Sanctuary): void {
    this.sanctuary = sanctuary;
  }
  private get range(): number {
    return this.cfg.range * this.rangeMult;
  }
  private get cooldownFull(): number {
    return this.cfg.cooldown * this.cdMult;
  }
  /** Alcance efetivo do hook (world units) — HUD/Character. */
  get effectiveRange(): number {
    return this.range;
  }
  get effectiveCooldown(): number {
    return this.cooldownFull;
  }
  private cooldown = 0;
  private sanctuary: Sanctuary | null = null;
  private readonly head = new THREE.Vector3();
  private readonly origin = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private travel = 0;
  private grabbed: HookTarget | null = null;

  // corrente com PESO: elos segmentados (InstancedMesh) em vez de uma linha fina (§3)
  private static readonly LINKS = 16;
  private readonly chain: THREE.InstancedMesh;
  private readonly headMesh: THREE.Mesh;
  private readonly raycaster = new THREE.Raycaster();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly _m = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _qRoll = new THREE.Quaternion();
  private readonly _p = new THREE.Vector3();
  private readonly _seg = new THREE.Vector3();
  private static readonly _ZAXIS = new THREE.Vector3(0, 0, 1);
  private static readonly _ZERO = new THREE.Vector3(0, 0, 0);

  constructor(
    scene: THREE.Scene,
    private readonly camera: THREE.PerspectiveCamera,
    private readonly input: GameInput,
  ) {
    // elo de corrente (torus achatado) — alterna 90° por elo pra leitura de "elos"
    const linkGeo = new THREE.TorusGeometry(0.13, 0.05, 5, 8);
    const linkMat = new THREE.MeshStandardMaterial({ color: 0xc4b2ff, emissive: 0x7c4dff, emissiveIntensity: 0.9, metalness: 0.55, roughness: 0.35, flatShading: true });
    this.chain = new THREE.InstancedMesh(linkGeo, linkMat, HookSystem.LINKS);
    this.chain.frustumCulled = false;
    this.chain.visible = false;
    scene.add(this.chain);

    this.headMesh = new THREE.Mesh(
      new THREE.IcosahedronGeometry(this.cfg.headRadius * 1.15, 0),
      new THREE.MeshStandardMaterial({ color: 0xd6c4ff, emissive: 0x7c4dff, emissiveIntensity: 1.4, metalness: 0.5, roughness: 0.3, flatShading: true }),
    );
    this.headMesh.visible = false;
    scene.add(this.headMesh);
  }

  get cooldownFraction(): number {
    return clamp(this.cooldown / Math.max(0.001, this.cooldownFull), 0, 1);
  }

  update(
    dt: number,
    playerPos: THREE.Vector3,
    enemies: readonly HookTarget[],
    preferred: HookTarget | null = null,
    combatEnabled = true,
    visualOrigin?: THREE.Vector3,
  ): void {
    this.origin.copy(visualOrigin ?? playerPos);
    if (!visualOrigin) this.origin.y += 1.05;
    this.cooldown = Math.max(0, this.cooldown - dt);
    if (this.state === "cooldown" && this.cooldown <= 0) this.state = "ready";

    // safe zone / buffer: hook bloqueado. Se estava em voo/puxando, cancela e retorna.
    if (!combatEnabled) {
      if (this.state === "firing" || this.state === "pulling") {
        this.release();
        this.state = "returning";
        this.cooldown = this.cfg.missCooldown;
      }
    } else if (this.state === "ready" && this.input.hookPressed()) {
      this.tryFire(playerPos, enemies, preferred);
    }

    if (this.state === "firing") this.updateFiring(dt, enemies);
    else if (this.state === "pulling") this.updatePulling(dt, playerPos);
    else if (this.state === "returning") this.updateReturning(dt);

    this.updateVisual();
  }

  private tryFire(playerPos: THREE.Vector3, enemies: readonly HookTarget[], preferred: HookTarget | null): void {
    const ndc = this.input.aimNdc;
    if (ndc) {
      // DEV: mira pelo mouse projetado no chão
      this.raycaster.setFromCamera(ndc, this.camera);
      if (!this.raycaster.ray.intersectPlane(this.plane, _hit)) return;
      _aim.subVectors(_hit, playerPos);
      _aim.y = 0;
    } else {
      // mobile: assistência de mira — prefere o alvo atual (auto-target) se fisgável e no alcance,
      // senão o alvo fisgável mais próximo. O alcance é sempre respeitado.
      const target = this.pickTarget(playerPos, enemies, preferred);
      if (!target) return;
      _aim.subVectors(target.position, playerPos);
      _aim.y = 0;
    }
    if (_aim.lengthSq() < 1e-4) return;
    this.dir.copy(_aim.normalize());
    this.head.copy(this.origin);
    this.travel = 0;
    this.grabbed = null;
    this.state = "firing";
    this.onThrow?.();
  }

  private pickTarget(
    playerPos: THREE.Vector3,
    enemies: readonly HookTarget[],
    preferred: HookTarget | null,
  ): HookTarget | null {
    const rangeSq = this.range * this.range;
    // 1) alvo atual (auto-target), se fisgável e dentro do alcance
    if (preferred && !preferred.isDead && !preferred.pulled) {
      const dx = preferred.position.x - playerPos.x;
      const dz = preferred.position.z - playerPos.z;
      if (dx * dx + dz * dz <= rangeSq) return preferred;
    }
    // 2) fisgável mais próximo dentro do alcance
    let best: HookTarget | null = null;
    let bestD = rangeSq;
    for (const e of enemies) {
      if (e.isDead || e.pulled) continue;
      const dx = e.position.x - playerPos.x;
      const dz = e.position.z - playerPos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD) {
        bestD = d2;
        best = e;
      }
    }
    return best;
  }

  private updateFiring(dt: number, enemies: readonly HookTarget[]): void {
    const step = this.cfg.projectileSpeed * dt;
    this.head.addScaledVector(this.dir, step);
    this.travel += step;

    for (const e of enemies) {
      if (e.isDead || e.pulled) continue;
      const dx = e.position.x - this.head.x;
      const dz = e.position.z - this.head.z;
      const reach = this.cfg.hitRadius + e.radius;
      if (dx * dx + dz * dz <= reach * reach) {
        const reaction = e.onHooked ? e.onHooked() : "pull";
        if (reaction === "pull") {
          this.grabbed = e;
          e.pulled = true;
          this.state = "pulling";
        } else {
          // "grab": fisgou mas não puxa (Elite/Boss já reagiram). "none": ricochete.
          this.state = "returning";
          this.cooldown = reaction === "grab" ? this.cooldownFull : this.cfg.missCooldown;
        }
        return;
      }
    }

    if (this.travel >= this.range) {
      this.state = "returning";
      this.cooldown = this.cfg.missCooldown; // errou: cooldown curto
    }
  }

  private updatePulling(dt: number, playerPos: THREE.Vector3): void {
    const e = this.grabbed;
    if (!e || e.isDead) {
      this.finishPull();
      return;
    }
    _dir.subVectors(playerPos, e.position);
    _dir.y = 0;
    const dist = _dir.length();
    // Distância mínima nunca menor que os raios somados: o inimigo JAMAIS termina dentro do player.
    const minDist = Math.max(this.cfg.minPullDistance, GameConfig.player.radius + e.radius + 0.15);
    if (dist <= minDist) {
      // snap: reposiciona exatamente em minDist ao longo da linha (sem sobreposição)
      if (dist > 1e-4) {
        _dir.multiplyScalar(1 / dist);
        e.position.set(playerPos.x - _dir.x * minDist, e.position.y, playerPos.z - _dir.z * minDist);
        this.sanctuary?.keepOut(e.position, e.radius + 0.2);
      }
      this.finishPull();
      return;
    }
    _dir.multiplyScalar(1 / dist);
    // criaturas pesadas (Craghorn/Warden) são puxadas mais devagar — sensação de PESO
    const pullFactor = (e as { hookPullFactor?: number }).hookPullFactor ?? 1;
    const step = Math.min(this.cfg.pullSpeed * pullFactor * dt, dist - minDist);
    e.position.addScaledVector(_dir, step);
    // nunca puxa o inimigo para dentro da safe zone (margem = raio do inimigo)
    this.sanctuary?.keepOut(e.position, e.radius + 0.2);
    this.head.set(e.position.x, 1, e.position.z);
  }

  private finishPull(): void {
    this.release();
    this.state = "returning";
    this.cooldown = this.cooldownFull; // acertou: cooldown normal
  }

  private updateReturning(dt: number): void {
    _dir.subVectors(this.origin, this.head);
    const dist = _dir.length();
    const step = this.cfg.projectileSpeed * 1.6 * dt;
    if (dist <= step || dist < 0.001) {
      this.state = this.cooldown > 0 ? "cooldown" : "ready";
      return;
    }
    this.head.addScaledVector(_dir.multiplyScalar(1 / dist), step);
  }

  private release(): void {
    if (this.grabbed) this.grabbed.pulled = false;
    this.grabbed = null;
  }

  private updateVisual(): void {
    const active = this.state === "firing" || this.state === "pulling" || this.state === "returning";
    this.chain.visible = active;
    this.headMesh.visible = active;
    if (!active) return;
    this.headMesh.position.copy(this.head);
    this.headMesh.rotation.y += 0.3; // giro leve do gancho

    // distribui elos ao longo de player(mão) -> cabeça; alterna 90° pra parecer corrente
    const start = this.origin;
    this._seg.subVectors(this.head, start);
    const len = this._seg.length();
    if (len < 1e-4) return;
    this._seg.multiplyScalar(1 / len);
    this._q.setFromUnitVectors(HookSystem._ZAXIS, this._seg);
    const n = Math.max(1, Math.min(HookSystem.LINKS, Math.ceil(len / 0.32)));
    const spacing = len / n;
    for (let i = 0; i < HookSystem.LINKS; i++) {
      if (i < n) {
        const t = spacing * (i + 0.5);
        this._qRoll.setFromAxisAngle(this._seg, (i % 2) * (Math.PI / 2));
        const q = this._qRoll.multiply(this._q);
        this._m.compose(
          this._p.copy(start).addScaledVector(this._seg, t),
          q,
          _LINK_SCALE,
        );
      } else {
        this._m.compose(start, this._q, HookSystem._ZERO); // esconde (escala 0)
      }
      this.chain.setMatrixAt(i, this._m);
    }
    this.chain.instanceMatrix.needsUpdate = true;
  }

  /** Chamado ao reiniciar/respawnar/morrer — solta o alvo e zera. */
  reset(): void {
    this.release();
    this.state = "ready";
    this.cooldown = 0;
    this.travel = 0;
    this.chain.visible = false;
    this.headMesh.visible = false;
  }
}
